import React from 'react';
import {
  Avatar,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material';
import { ChildCare, Clear, Home } from '@mui/icons-material';
import { getRecentUsers, clearRecentUsers } from '../../util/recentUsersStorage';

interface RecentlyAccessedUsersProps {
  onSelectUser: (userId: number) => void;
}

function RecentlyAccessedUsers({ onSelectUser }: RecentlyAccessedUsersProps) {
  const [recentUsers, setRecentUsers] = React.useState(getRecentUsers());

  const handleClear = () => {
    clearRecentUsers();
    setRecentUsers([]);
  };

  if (recentUsers.length === 0) {
    return null;
  }

  return (
    <Card sx={{ mb: 2 }}>
      <CardHeader
        title='Recently Accessed Users'
        action={
          <Button size='small' startIcon={<Clear />} onClick={handleClear}>
            Clear
          </Button>
        }
      />
      <CardContent sx={{ pt: 0 }}>
        <List dense>
          {recentUsers.map(user => (
            <ListItem
              key={user.id}
              onClick={() => onSelectUser(user.id)}
              sx={{
                cursor: 'pointer',
                '&:hover': { backgroundColor: 'action.hover' },
                borderRadius: 1,
              }}
            >
              <ListItemAvatar>
                <Avatar src={user.pictureUrl} alt={user.name}>
                  {user.name?.charAt(0) || '?'}
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Typography variant='body2'>
                    {user.name || 'Unknown'}
                    <Typography
                      component='span'
                      variant='caption'
                      color='text.secondary'
                      sx={{ ml: 1 }}
                    >
                      #{user.id}
                    </Typography>
                  </Typography>
                }
                secondary={user.email}
              />
              {user.parent && (
                <Tooltip title='Parent'>
                  <Chip
                    icon={<Home fontSize='small' />}
                    label='Parent'
                    size='small'
                    color='primary'
                    variant='outlined'
                    sx={{ mr: 0.5 }}
                  />
                </Tooltip>
              )}
              {user.sitter && (
                <Tooltip title='Sitter'>
                  <Chip
                    icon={<ChildCare fontSize='small' />}
                    label='Sitter'
                    size='small'
                    color='secondary'
                    variant='outlined'
                  />
                </Tooltip>
              )}
            </ListItem>
          ))}
        </List>
      </CardContent>
    </Card>
  );
}

export default RecentlyAccessedUsers;
